import { useRef, useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import Button from '../components/Button';
import Spinner from '../components/Spinner';
import { useAuthStore } from '../stores/auth';

interface LocationState {
  from?: string;
}

/**
 * 登录页（深空主题，居中卡片）。
 *
 * - 登录成功 → 跳 /map（若由守卫跳转而来，则回跳原路径）
 * - 已登录访问本页 → 直接重定向 /map
 */
export default function LoginPage() {
  const token = useAuthStore((s) => s.token);
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const navigate = useNavigate();
  const location = useLocation();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  if (token) return <Navigate to="/map" replace />;

  const togglePassword = () => {
    setShowPassword((v) => !v);
    passwordRef.current?.focus();
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await login(username.trim(), password);
      const from = (location.state as LocationState | null)?.from;
      navigate(from && from !== '/login' ? from : '/map', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登录失败，请稍后重试');
    }
  };

  return (
    <div className="login">
      <form className="login__card" onSubmit={onSubmit}>
        <div className="login__brand">
          <span className="mark">
            <span>A</span>
          </span>
          <span className="txt">
            <b>ATLAS PM</b>
            <span>寰图 · 全国运营项目管理平台</span>
          </span>
        </div>

        {error && (
          <div className="login__error" role="alert">
            {error}
          </div>
        )}

        <div className="field">
          <label className="field__label" htmlFor="username">
            用户名
          </label>
          <input
            id="username"
            className="field__input"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            placeholder="请输入用户名"
            required
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="password">
            密码
          </label>
          <div className="field__password">
            <input
              id="password"
              ref={passwordRef}
              className="field__input"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              placeholder="请输入密码"
              required
            />
            <button
              type="button"
              className="field__eye"
              aria-label={showPassword ? '隐藏密码' : '显示密码'}
              aria-pressed={showPassword}
              onMouseDown={(e) => e.preventDefault()}
              onClick={togglePassword}
            >
              {showPassword ? (
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="M3 3l18 18M10.6 10.6a2 2 0 002.8 2.8M9.4 5.2A9.9 9.9 0 0112 5c4.5 0 8.3 2.8 10 7a10.6 10.6 0 01-2.6 3.4M6.1 6.1A10.6 10.6 0 002 12c1.7 4.2 5.5 7 10 7a9.9 9.9 0 003.4-.6M3 3l18 18"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
                  <path
                    d="M2 12c1.7-4.2 5.5-7 10-7s8.3 2.8 10 7c-1.7 4.2-5.5 7-10 7s-8.3-2.8-10-7z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="3"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>

        <Button type="submit" variant="primary" size="lg" block disabled={loading}>
          {loading ? <Spinner size="sm" /> : '进入指挥中心'}
        </Button>
      </form>
    </div>
  );
}
